"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import EmergencySupportCard from "@/app/(landing)/fees-funding/components/emergency-support-card";
import { motion } from "framer-motion";

/**
 * Props for `EmergencySupport`.
 */
export type EmergencySupportProps =
  SliceComponentProps<Content.EmergencySupportSlice>;

/**
 * Component for "EmergencySupport" Slices.
 */
const EmergencySupport: FC<EmergencySupportProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {slice.variation === "default" ? (
        <div className="bg-[#FAFDFF]">
          <Bounded className="py-20">
            {/* header */}

            <div className="flex flex-col items-center text-primary gap-4 mb-10">
              {/* heading */}

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                viewport={{ once: true }}
              >
                <PrismicRichText
                  field={slice.primary.heading}
                  components={{
                    heading1: ({ children }) => (
                      <h1 className="text-4xl md:text-5xl font-semibold md:text-center">
                        {children}
                      </h1>
                    ),
                    heading2: ({ children }) => (
                      <h2 className="text-4xl md:text-5xl font-semibold md:text-center">
                        {children}
                      </h2>
                    ),
                    heading3: ({ children }) => (
                      <h3 className="text-4xl md:text-5xl font-semibold md:text-center">
                        {children}
                      </h3>
                    ),
                    heading4: ({ children }) => (
                      <h4 className="text-4xl md:text-5xl font-semibold md:text-center">
                        {children}
                      </h4>
                    ),
                    heading5: ({ children }) => (
                      <h5 className="text-4xl md:text-5xl font-semibold md:text-center">
                        {children}
                      </h5>
                    ),
                    heading6: ({ children }) => (
                      <h6 className="text-4xl md:text-5xl font-semibold md:text-center">
                        {children}
                      </h6>
                    ),
                    label: ({ node, children }) => {
                      if (node.data.label === "Highlight") {
                        return (
                          <span className="text-secondary">{children}</span>
                        );
                      }
                    },
                  }}
                />
              </motion.div>

              {/* subheading */}

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.8, ease: "easeInOut" }}
                viewport={{ once: true }}
              >
                <PrismicRichText
                  field={slice.primary.sub_heading}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="font-merriweather text-lg leading-8">
                        {children}
                      </p>
                    ),
                  }}
                />
              </motion.div>
            </div>

            {/* cards */}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2.5">
              {slice.primary.support_cards.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{
                    duration: 0.4,
                    ease: "easeInOut",
                  }}
                  viewport={{ once: true }}
                >
                  <EmergencySupportCard key={index} data={item} />
                </motion.div>
              ))}
            </div>
          </Bounded>
        </div>
      ) : null}
    </section>
  );
};

export default EmergencySupport;
